import { apiService } from "../../services/ApiService";
import { endpoints, porterEndpoints } from "../../utils/endpoints";
import { getUrlWithCustomFilters } from "../../utils/util";
import { OrderData, PorterQuotePayload, PorterQuoteResponse } from "./types";
import { ConfigurationKeys } from "../../config/ApiConfig";
import { getGlobalApiConfig } from "../../config/ApiConfig";
import { buildCptSlabLevelParams } from "../../utils/util";

export const getDispatchCustomerFacets = async (
  paramsObj: Record<string, any> = {},
  activeTab?: string,
  activeSupplierId?: string,
) => {
  const cptSlabParams = buildCptSlabLevelParams(activeTab, paramsObj);
  const defaultParams = {
    ...paramsObj,
    orderStatus: ["READY_TO_DISPATCH"],
    ...cptSlabParams,
    facets: "customers",
    limit: "1",
    isPopulated: "true",
    shippedOrders: "false",
  };

  const customParams =
    getGlobalApiConfig()?.customFilters?.[
      ConfigurationKeys.getDispatchCustomerFacets
    ] ||
    getGlobalApiConfig()?.customFilters?.[
      ConfigurationKeys.getR2DCustomerFacets
    ];

  const url = getUrlWithCustomFilters(
    defaultParams,
    endpoints.getR2DOrders,
    customParams?.filters,
    customParams?.endpoint,
    activeSupplierId ? { activeSupplierId } : undefined,
  );

  const response = await apiService.axiosCruds().get(url);

  return response?.data?.facets ?? { customers: [] };
};

export const getDispatchSupplierFacets = async (
  customerId?: string,
  paramsObj: Record<string, any> = {},
  activeTab?: string,
) => {
  const cptSlabParams = buildCptSlabLevelParams(activeTab, paramsObj);
  const defaultParams = {
    ...paramsObj,
    orderStatus: ["READY_TO_DISPATCH"],
    ...cptSlabParams,
    facets: "suppliers",
    limit: "1",
    isPopulated: "true",
    shippedOrders: "false",
  };
  const customParams =
    getGlobalApiConfig()?.customFilters?.[
      ConfigurationKeys.getDispatchSupplierFacets
    ] ||
    getGlobalApiConfig()?.customFilters?.[
      ConfigurationKeys.getR2DSupplierFacets
    ];

  const url = getUrlWithCustomFilters(
    defaultParams,
    endpoints.getR2DOrders,
    customParams?.filters,
    customParams?.endpoint,
    customerId ? { customerId } : undefined,
  );

  const response = await apiService.axiosCruds().get(url);

  return response?.data?.facets ?? { suppliers: [] };
};

export const getDispatchOrdersForSupplier = async (
  customerId: string,
  supplierId: string,
  activeTab?: string,
  paramsObj?: Record<string, any>,
) => {
  const cptSlabParams = buildCptSlabLevelParams(activeTab, paramsObj);
  const defaultParams = {
    ...paramsObj,
    orderStatus: ["READY_TO_DISPATCH"],
    ...cptSlabParams,
    isPopulated: "true",
    limit: "50",
    shippedOrders: "false",
  };

  const customParams =
    getGlobalApiConfig()?.customFilters?.[
      ConfigurationKeys.getDispatchOrdersForSupplier
    ] ||
    getGlobalApiConfig()?.customFilters?.[
      ConfigurationKeys.getR2DOrdersForSupplier
    ];

  const url = getUrlWithCustomFilters(
    defaultParams,
    endpoints.getR2DOrders,
    customParams?.filters,
    customParams?.endpoint,
    { customerId, activeSupplierId: supplierId },
  );

  const response = await apiService.axiosCruds().get(url);

  return response?.data ?? { orders: [], count: 0 };
};

export const mapOrderToComponentFormat = (apiOrder: any): OrderData => {
  const allocation =
    apiOrder.allocatedTo?.find((a: any) => a.isActive) ||
    apiOrder.allocatedTo?.[0];

  return {
    _id: apiOrder._id,
    orderNo: apiOrder.orderNo,
    customer: {
      name: apiOrder.customer?.name,
      mobileNumber: apiOrder.customer?.mobileNumber,
    },
    orderDate: apiOrder.orderDate,
    orderStatus: apiOrder.orderStatus,
    totalItems: apiOrder.orderItems?.length || 0,
    totalQuantity:
      apiOrder.orderItems?.reduce(
        (sum: number, item: any) => sum + (item.givenQuantity || 0),
        0,
      ) || 0,
    orderValue: parseFloat(allocation?.invoiceInfo?.invoiceAmount || "0"),
    deliveryMode: apiOrder.deliveryMode,
    shipmentAddress: {
      addressLine1: apiOrder.shipmentAddress?.addressLine1 || "",
      city: apiOrder.shipmentAddress?.city || "",
      state: apiOrder.shipmentAddress?.state || "",
    },
    allocatedTo: apiOrder.allocatedTo || [],
    isUrgent: apiOrder.isUrgent || false,
    orderETA: apiOrder.orderETA || [],
    invoiceInfo: allocation?.invoiceInfo
      ? {
          invoiceMediaId: allocation.invoiceInfo.invoiceMediaId,
          invoiceNo: allocation.invoiceInfo.invoiceNo,
          invoiceAmount: allocation.invoiceInfo.invoiceAmount,
          invoiceDate: allocation.invoiceInfo.invoiceDate,
          noOfItems: allocation.invoiceInfo.noOfItems,
          totalQtyOfItems: allocation.invoiceInfo.totalQtyOfItems,
          invoiceStatus: allocation.invoiceInfo.invoiceStatus,
        }
      : undefined,
    garaazInvoiceInfo: apiOrder?.invoiceInfo
      ? {
          invoiceMediaId: apiOrder.invoiceInfo.invoiceMediaId,
          invoiceNo: apiOrder.invoiceInfo.invoiceNo,
          invoiceAmount: apiOrder.invoiceInfo.invoiceAmount,
          invoiceDate: apiOrder.invoiceInfo.invoiceDate,
          noOfItems: apiOrder.invoiceInfo.noOfItems,
          totalQtyOfItems: apiOrder.invoiceInfo.totalQtyOfItems,
          invoiceStatus: apiOrder.invoiceInfo.invoiceStatus,
        }
      : undefined,
    challanInfo: allocation?.challanInfo
      ? {
          challanMediaId: allocation.challanInfo.challanMediaId,
          challanNo: allocation.challanInfo.challanNo,
          challanAmount: allocation.challanInfo.challanAmount,
          challanDate: allocation.challanInfo.challanDate,
        }
      : undefined,
    PPDAmount: (() => {
      const n = Number(apiOrder.PPDAmount);
      return Number.isFinite(n) ? n : undefined;
    })(),
    orderItems: apiOrder.orderItems || [],
    dispatchRider: apiOrder.metaInfo?.dispatchRider
      ? {
          name: apiOrder.metaInfo.dispatchRider.name || "",
          phone: apiOrder.metaInfo.dispatchRider.phone || "",
          vehicleNumber: apiOrder.metaInfo.dispatchRider.vehicleNumber || "",
          trackingUrl: apiOrder.metaInfo.dispatchRider.trackingUrl || "",
        }
      : undefined,
  };
};

export const getPorterQuote = async (
  payload: PorterQuotePayload,
): Promise<PorterQuoteResponse> => {
  try {
    const response = await apiService
      .baseAxiosCruds()
      .post<PorterQuoteResponse>(porterEndpoints.getPorterQuote, payload);

    return response.data || { vehicles: [] };
  } catch (err: any) {
    const apiMessage =
      err?.response?.data?.errorMsg ||
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      "Failed to fetch porter quote";
    const customError = new Error(apiMessage);
    (customError as any).response = err?.response;
    (customError as any).status = err?.response?.status;

    throw customError;
  }
};

export const getGrazeons = async (query?: { userSubType?: string }) => {
  const params = new URLSearchParams();
  if (query?.userSubType) {
    params.append("userSubType", query.userSubType);
  }

  const response = await apiService
    .axiosCruds()
    .get(
      `${endpoints.getGrazeons}${
        params.toString() ? `?${params.toString()}` : ""
      }`,
    );
  return response.data;
};
